/**
 * Loading & Skeleton Components — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - LoadingSpinner: Animated spinner for loading states
 * - Skeleton: Placeholder for content loading
 * - Different sizes and variants
 * - Uses semantic theme tokens
 */

import { HTMLAttributes } from 'react';

export type LoadingSize = 'sm' | 'md' | 'lg' | 'xl';

export interface LoadingSpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: LoadingSize;
  text?: string;
}

const spinnerSizes: Record<LoadingSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

export function LoadingSpinner({
  size = 'md',
  text,
  className = '',
  ...props
}: LoadingSpinnerProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 ${className}`}
      role="status"
      aria-live="polite"
      {...props}
    >
      <svg
        className={`animate-spin text-[var(--reviewer-accent)] ${spinnerSizes[size]}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {text && (
        <p className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono">
          {text}
        </p>
      )}
    </div>
  );
}

/**
 * Skeleton Loader
 */
export type SkeletonVariant = 'text' | 'rectangular' | 'circular';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  count?: number;
}

export function Skeleton({
  variant = 'text',
  width,
  height,
  count = 1,
  className = '',
  style,
  ...props
}: SkeletonProps) {
  const baseStyles = `
    bg-[var(--reviewer-bg-tertiary)]
    animate-pulse
  `;

  const variantStyles: Record<SkeletonVariant, string> = {
    text: 'h-4',
    rectangular: 'h-24',
    circular: 'rounded-full w-12 h-12',
  };

  const skeletonStyle = {
    width: width ? (typeof width === 'number' ? `${width}px` : width) : undefined,
    height: height ? (typeof height === 'number' ? `${height}px` : height) : undefined,
    ...style,
  };

  if (count > 1) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className={`${baseStyles} ${variantStyles[variant]} ${className}`}
            style={skeletonStyle}
            {...props}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      style={skeletonStyle}
      {...props}
    />
  );
}
